import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Divider, Form, Row } from "antd";
import AppInput from "../../../common/AppInput";
import { BulletList } from "../../../common/BulletTextArea";
import { isEmpty, map, sumBy } from "lodash";
import dayjs from "dayjs";
import appConfig from "../../../../config/config.json";
import moment from "moment";

const PlainText = ({ value }) => (
  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
    {value}
  </div>
);

const PrintActivityReportingTemplate = () => {
  const {
    advanceAccountabilityFormsDetails,
    advanceAccountabilityFormsExpenditure: actualExpenditure,
  } = useSelector((state) => state.advanceAccountabilityFormsState);

  const reportingTemplate = useMemo(
    () => advanceAccountabilityFormsDetails.reportingTemplate || null,
    [advanceAccountabilityFormsDetails.reportingTemplate]
  );

  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue({
      ...reportingTemplate,
      indicator_id: map(
        reportingTemplate?.activityReportingTemplateIndicators,
        "indicator_id"
      ),
      indicators: map(
        reportingTemplate?.activityReportingTemplateIndicators,
        (indicator) => ({
          indicator_id: indicator.indicator_id,
          score: indicator.score,
        })
      ),
      date: dayjs(reportingTemplate?.date),
    });
  }, [reportingTemplate, form]);

  const count = (field) => Number(reportingTemplate?.[field]) || 0;
  const ageGroups = ["0_12", "13_19", "20_35", "above_35"];
  const rowTotal = (prefix) =>
    sumBy(ageGroups, (age) => count(`${prefix}_${age}`));
  const columnTotal = (age) =>
    sumBy(
      ["male_ugandan", "female_ugandan", "male_refugee", "female_refugee"],
      (prefix) => count(`${prefix}_${age}`)
    );
  const grandTotal = sumBy(ageGroups, columnTotal);

  const totalActualExpenditure = sumBy(
    actualExpenditure,
    "actual_expenditure"
  )?.toLocaleString();
  return (
    <div>
      <p className="text-center h5 text-uppercase">
        Amani Initiative Activity Report
      </p>
      <Form form={form}>
        <table className="table table-sm table-bordered">
          <tbody>
            <tr>
              <td className="w-50 fw-bold">Project/initiative name</td>
              <td>
                {advanceAccountabilityFormsDetails.projectDetails?.project_name}
              </td>
            </tr>
            <tr>
              <td className="w-50 fw-bold">Title of activity</td>
              <td>
                {advanceAccountabilityFormsDetails?.budgetLineDetails?.name}
              </td>
            </tr>
            {/* <tr>
              <td className="w-50 fw-bold">Strategic plan priority Input</td>
              <td>{advanceAccountabilityFormsExpenditureDetails.name}</td>
            </tr> */}
            <tr>
              <td className="w-50 fw-bold">Strategic plan indicator</td>
              <td>
                <AppInput
                  type="select"
                  name="indicator_id"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "You can select the one that apply.",
                    mode: "multiple",
                  }}
                  options={map(
                    advanceAccountabilityFormsDetails.strategicPlanIndicators,
                    (indicator) => ({
                      label: indicator.indicator,
                      value: indicator.id,
                    })
                  )}
                />
              </td>
            </tr>

            <Form.List name="indicators">
              {(fields) => (
                <>
                  {fields.map((field, index) => (
                    <tr key={field.key}>
                      <td>
                        <AppInput
                          type="select"
                          name={[field.name, "indicator_id"]}
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "You can select the one that apply.",
                            readOnly: true,
                          }}
                          options={map(
                            advanceAccountabilityFormsDetails.strategicPlanIndicators,
                            (indicator) => ({
                              label: indicator.indicator,
                              value: indicator.id,
                              disabled: true,
                            })
                          )}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name={[field.name, "score"]}
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "enter a value",
                            min: 0,
                          }}
                        />
                      </td>
                    </tr>
                  ))}{" "}
                </>
              )}
            </Form.List>
            <tr>
              <td className="w-50 fw-bold">Funder</td>
              <td>
                {advanceAccountabilityFormsDetails?.projectDetails?.donor_name}
              </td>
            </tr>
            <tr>
              <td className="w-50 fw-bold">Dates and duration </td>
              <td>
                <Row>
                  <span className="mx-2">Start Date:</span>{" "}
                  <AppInput
                    type="date"
                    name="date"
                    inputAttributes={{
                      variant: "borderless",
                    }}
                  />
                  <span className="mx-2"> Duration:</span>
                  <AppInput
                    type="number"
                    name="duration"
                    inputAttributes={{
                      variant: "borderless",
                      placeholder: "Enter Period",
                    }}
                  />
                  <AppInput
                    type="select"
                    name="duration_unit"
                    inputAttributes={{
                      variant: "borderless",
                      placeholder: "Select Period Type",
                    }}
                    options={[
                      { label: "Hours(s)", value: "hours" },
                      { label: "Day(s)", value: "days" },
                      { label: "Week(s)", value: "weeks" },
                      { label: "Month(s)", value: "months" },
                    ]}
                  />
                </Row>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Activity Venue </td>
              <td>
                <Form.Item name="venue" noStyle>
                  <PlainText />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Description of the activity </td>
              <td>
                <Form.Item name="achievements_and_results" noStyle>
                  <BulletList />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Activity achievements and results </td>
              <td>
                <Form.Item name="description" noStyle>
                  <BulletList />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Challenges encountered and how these challenges were overcome{" "}
              </td>
              <td>
                <Form.Item name="challenges" noStyle>
                  <BulletList />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Key lessons </td>
              <td>
                <Form.Item name="key_lessons" noStyle>
                  <BulletList />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Recommendations </td>
              <td>
                <Form.Item name="recommendations" noStyle>
                  <BulletList />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Number of project participants or intermediaries reached{" "}
              </td>
              <td>
                <table class="table table-bordered table-sm ">
                  <thead class="">
                    <tr>
                      <th>Sex</th>
                      <th>0-12</th>
                      <th>13-19</th>
                      <th>20-35</th>
                      <th>Above 35</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Male Ugandan</td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{rowTotal("male_ugandan")}</td>
                    </tr>
                    <tr>
                      <td>Female Ugandan</td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{rowTotal("female_ugandan")}</td>
                    </tr>
                    <tr>
                      <td>Male Refugee</td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{rowTotal("male_refugee")}</td>
                    </tr>
                    <tr>
                      <td>Female Refugee</td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{rowTotal("female_refugee")}</td>
                    </tr>
                    <tr className="fw-bold">
                      <td>Total</td>
                      {map(ageGroups, (age) => (
                        <td key={age} className="text-center align-middle">
                          {columnTotal(age)}
                        </td>
                      ))}
                      <td className="text-center align-middle">{grandTotal}</td>
                    </tr>
                  </tbody>
                </table>
                <Divider>Person with a disability</Divider>
                <table className="table table-sm table-bordered">
                  <tr>
                    <td>Male</td>
                    <td>
                      <AppInput
                        type="number"
                        name="persons_with_disability_male"
                        inputAttributes={{
                          variant: "borderless",
                          placeholder: "eg 2",
                          min: 0,
                        }}
                      />
                    </td>
                    <td>Female</td>
                    <td>
                      {" "}
                      <AppInput
                        type="number"
                        name="persons_with_disability_female"
                        inputAttributes={{
                          variant: "borderless",
                          placeholder: "eg 2",
                          min: 0,
                        }}
                      />
                    </td>
                    <td>Total</td>
                    <td className="fw-bold text-center align-middle">
                      {count("persons_with_disability_male") +
                        count("persons_with_disability_female")}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Community Voice (Quotes) </td>
              <td>
                <Form.Item name="community_voice" noStyle>
                  <PlainText />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Success Story{" "}
                <span className="text-muted fw-normal">
                  (Brief success story or human interest story highlighting the
                  impact of this activity)
                </span>
              </td>
              <td>
                <Form.Item name="success_story" noStyle>
                  <PlainText />
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>

        {!isEmpty(reportingTemplate) &&
          !isEmpty(reportingTemplate?.actionPoints) && (
            <>
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>ACTION POINT</th>
                    <th>PERSON RESPONSIBLE</th>
                    <th>TIMELINE</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {map(reportingTemplate?.actionPoints, (point, index) => (
                    <tr key={index}>
                      <td>{point.action_point}</td>
                      <td>{point.person_responsible}</td>
                      <td>{point.timeline}</td>
                      <td>{point.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

        <p className="fw-bold">Report sign off.</p>
        <table className="table table-bordered">
          <tbody>
            <tr>
              <td className="fw-bold">Payment Voucher Number</td>
              <td>{advanceAccountabilityFormsDetails.id}</td>
            </tr>
            <tr>
              <td className="fw-bold">Amount advanced</td>
              <td>
                UGX{" "}
                {advanceAccountabilityFormsDetails?.amountAdvanced?.toLocaleString()}
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Amount actually used</td>
              <td>UGX {totalActualExpenditure || 0}</td>
            </tr>
            <tr>
              <td className="fw-bold">Staff in-charge</td>
              <td>
                {reportingTemplate?.created_by?.first_name}{" "}
                {reportingTemplate?.created_by?.other_names}
              </td>
            </tr>
            <tr>
              <td>Prepared By</td>
              <td className="fw-bold">
                {reportingTemplate?.created_by?.first_name}{" "}
                {reportingTemplate?.created_by?.other_names}
              </td>
            </tr>
            <tr>
              <td>
                Signature
                <div>
                  {reportingTemplate?.created_by?.signature && (
                    <img
                      src={`${appConfig.server_url}${reportingTemplate?.created_by?.signature}`}
                      alt="signature"
                      width="30"
                      height="15"
                    />
                  )}
                </div>
              </td>
              <td className="fw-bold">
                Date Prepared:{" "}
                {moment(reportingTemplate?.created_at).format("DD/MM/YYYY")}
              </td>
            </tr>
            <tr>
              <td>Checked By</td>
              <td className="fw-bold">
                {reportingTemplate?.checked_by?.first_name}{" "}
                {reportingTemplate?.checked_by?.other_names}
              </td>
            </tr>
            <tr>
              <td>
                Signature
                <div>
                  {reportingTemplate?.checked_by?.signature && (
                    <img
                      src={`${appConfig.server_url}${reportingTemplate?.checked_by?.signature}`}
                      alt="signature"
                      width="30"
                      height="15"
                    />
                  )}
                </div>
              </td>
              <td>
                Date Checked:{" "}
                {moment(reportingTemplate?.checked_at).format("DD/MM/YYYY")}
              </td>
            </tr>
            <tr>
              <td>Approved By</td>
              <td>
                {reportingTemplate?.approved_by?.first_name}{" "}
                {reportingTemplate?.approved_by?.other_names}
              </td>
            </tr>
            <tr>
              <td>
                Signature
                <div>
                  {reportingTemplate?.approved_by?.signature && (
                    <img
                      src={`${appConfig.server_url}${reportingTemplate?.approved_by?.signature}`}
                      alt="signature"
                      width="30"
                      height="15"
                    />
                  )}
                </div>
              </td>
              <td>
                Date Approved:{" "}
                {moment(reportingTemplate?.approved_at).format("DD/MM/YYYY")}
              </td>
            </tr>
          </tbody>
        </table>
      </Form>
    </div>
  );
};

export default PrintActivityReportingTemplate;
